import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  useTheme,
  InputAdornment,
} from "@mui/material";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ApplicationType,
  entity_id,
  getGeolocation,
  Platform,
} from "../Utils/Const";
import useIPAddress from "../Utils/useIPAddress";
import { baseapi } from "../Environmentalurl";
import { useAuthdata } from "../Utils/AuthContext";
import { saveState } from "../Utils/sessionUtility";
import { useDispatch, useSelector } from "react-redux";
import {
  setUser,
  setBbpsUrls,
  setRupayRefNo,
  setSubWallets,
  setLoginMeta,
} from "../Slice/authSlice";
import { setAccountNo } from "../Slice/authSlice";
import { Link } from "react-router-dom";
import { Stack, useMediaQuery } from "@mui/system";
import api from "../Utils/Api";
import { setToken } from "../Utils/tokenService";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CreateTpinSetup from "./CreateTpinSetup";
import SetSecurityQuestionsSetup from "./SetSecurityQuestionsSetup";
import SetRupayPinSetup from "./SetRupayPinSetup";

const VALIDATE_TOKEN_URL = `${baseapi}/auth/validatetoken`;
const LOGIN_WITH_TPIN_URL = `${baseapi}/auth/loginwithtpin`;
const GENERATE_OTP_URL = `${baseapi}/auth/generateotp`;
const VALIDATE_OTP_URL = `${baseapi}/auth/validateotp`;
const TPIN_MANAGEMENT_URL = `${baseapi}/auth/tpin`;
const STATIC_GEOLOCATION = "17.385044,78.486671";

const maskMobile = (num) => {
  if (!num || num.length < 6) return num;
  const visible = num.slice(-4);
  const masked = num.slice(0, -4).replace(/./g, "X");
  return masked + visible;
};

const OtpPage = () => {
  // ─── screen can be: "checking" | "otp" | "tpin" | "switch-number"
  //                  | "forgot-tpin-otp" | "forgot-tpin-set" ──────────────────
  const [screen, setScreen] = useState("checking");

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(60);
  const navigate = useNavigate();
  const location = useLocation();
  const ip = useIPAddress();
  const { login } = useAuthdata();
  const [geo, setGeo] = useState("");
  const [pendingLoginData, setPendingLoginData] = useState(null);

  const [tpin, setTpin] = useState(["", "", "", ""]);
  const [verifyingTpin, setVerifyingTpin] = useState(false);

  // ── "Login with another number" state ──────────────────────────────────────
  const [altPhone, setAltPhone] = useState("");
  const [altPhoneError, setAltPhoneError] = useState(false);
  const [sendingAltOtp, setSendingAltOtp] = useState(false);

  // ── Forgot TPIN state ───────────────────────────────────────────────────────
  const [forgotOtp, setForgotOtp] = useState(["", "", "", "", "", ""]);
  const [forgotOtpTimer, setForgotOtpTimer] = useState(60);
  const [forgotOtpShowResend, setForgotOtpShowResend] = useState(false);
  const [forgotOtpMsgRefId, setForgotOtpMsgRefId] = useState("");
  const [verifyingForgotOtp, setVerifyingForgotOtp] = useState(false);
  const [generatingForgotOtp, setGeneratingForgotOtp] = useState(false);

  const [newTpin, setNewTpin] = useState(["", "", "", ""]);
  const [confirmTpin, setConfirmTpin] = useState(["", "", "", ""]);
  const [settingTpin, setSettingTpin] = useState(false);
  const [pendingSteps, setPendingSteps] = useState([]);

  useEffect(() => {
    const loadGeo = async () => {
      const location = await getGeolocation();
      setGeo(location);
    };
    loadGeo();
  }, []);

  const dispatch = useDispatch();
  const [showResend, setShowResend] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const loginMeta = useSelector((state) => state.auth.loginMeta);
  const { messageRefId, mobileNumber: reduxMobile } = loginMeta ?? {};

  const routeStateMobile = location.state?.mobileFromToken;

  const [activeMobile, setActiveMobile] = useState(
    () =>
      routeStateMobile ||
      reduxMobile ||
      sessionStorage.getItem("mobileNumber") ||
      "",
  );

  const mobileNumber = activeMobile;

  const successToastStyle = {
    background: "green",
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
  };

  const errorToastStyle = {
    background: "red",
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
  };

  const isExpired = (expiryTimeStr) => {
    try {
      const [datePart, timePart] = expiryTimeStr.split(" ");
      const [day, month, year] = datePart.split("-").map(Number);
      const expiryDate = new Date(
        year,
        month - 1,
        day,
        ...(timePart ? timePart.split(":").map(Number) : [0, 0, 0]),
      );
      return Date.now() > expiryDate.getTime();
    } catch (err) {
      return true;
    }
  };

  useEffect(() => {
    checkWalletTokenFlow();
  }, []);

  const checkWalletTokenFlow = async () => {
    // ✅ FIX: check the durable sessionStorage flag FIRST, not just
    // location.state. Loginpage.js sets this flag at the exact moment it
    // confirms (via validatetoken) that the WalletToken is VALID, right
    // before navigating here. Route `state` is passed too, but state can
    // be lost across certain navigation/remount sequences — when that
    // happens, relying on state ALONE caused this component to fall
    // through to a second, redundant validatetoken call, which could (for
    // reasons independent of the token's actual validity) end up routing
    // to the OTP screen instead of TPIN, even though the token was
    // genuinely valid. Checking the flag here means we trust the decision
    // Loginpage.js already made, instead of re-deciding from scratch.
    const tokenAlreadyValidated =
      sessionStorage.getItem("walletTokenValidated") === "true";

    if (tokenAlreadyValidated) {
      sessionStorage.removeItem("walletTokenValidated"); // consume it once
      setScreen("tpin");
      return;
    }

    if (location.state?.mobileFromToken && location.state?.walletToken) {
      setScreen("tpin");
      return;
    }

    const walletToken = localStorage.getItem("SessionWalletToken");

    if (!walletToken) {
      setScreen("otp");
      return;
    }

    try {
      const validateResponse = await axios.post(VALIDATE_TOKEN_URL, {
        token: walletToken,
      });

      const data = validateResponse.data;

      if (data?.status === "VALID" && !isExpired(data?.expiryTime)) {
        const mobileFromValidate = data.mobileNumber;

        if (mobileFromValidate) {
          sessionStorage.setItem("mobileNumber", mobileFromValidate);
        }

        const mobile =
          mobileFromValidate ||
          reduxMobile ||
          sessionStorage.getItem("mobileNumber");

        if (!mobile) {
          setScreen("otp");
        } else {
          setScreen("tpin");
        }
      } else {
        setScreen("otp");
      }
    } catch (error) {
      setScreen("otp");
    }
  };

  // Countdown for main OTP screen
  useEffect(() => {
    if (screen !== "otp") return;
    if (timer > 0) {
      const countdown = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(countdown);
    } else {
      setShowResend(true);
    }
  }, [timer, screen]);

  // Countdown for forgot-tpin OTP screen
  useEffect(() => {
    if (screen !== "forgot-tpin-otp") return;
    if (forgotOtpTimer > 0) {
      const countdown = setInterval(
        () => setForgotOtpTimer((prev) => prev - 1),
        1000,
      );
      return () => clearInterval(countdown);
    } else {
      setForgotOtpShowResend(true);
    }
  }, [forgotOtpTimer, screen]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      next?.focus();
    }
  };

  const handleResendOTP = async () => {
    try {
      const payload = {
        ReqType: "One_loginResendOTP",
        MobileNumber: mobileNumber,
        EntityID: entity_id,
        SSOID: "-",
        MemberID: "-",
        InputMessageRefID: messageRefId,
        Statement: "LoginWithOTP",
        MachineID: ip,
        Platform,
        Geolocation: geo,
      };

      const response = await api.post(`${baseapi}/auth/loginresend`, payload);

      if (response.data.Response_Code === "1000") {
        toast.success("OTP resent successfully!", {
          style: successToastStyle,
          position: "top-center",
        });
        setTimer(60);
        setShowResend(false);
      } else {
        toast.error(response.data.Message || "Failed to resend OTP", {
          style: errorToastStyle,
          position: "top-center",
        });
      }
    } catch (error) {
      toast.error("Error sending OTP", {
        style: errorToastStyle,
        position: "top-center",
      });
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prev = document.getElementById(`otp-${index - 1}`);
      prev?.focus();
    }
  };

  const handleLoginSuccess = (responseData) => {
    let userData = null;
    dispatch(setUser(userData));
    try {
      const customerProfileString = responseData.Info?.[0]?.CustomerProfile;
      if (customerProfileString) {
        const parsed = JSON.parse(customerProfileString);
        userData = parsed[0];
        const profile = JSON.parse(responseData.Info[0].CustomerProfile);
        const rupayRef = profile[0].RupayRefNo;
        dispatch(setRupayRefNo(rupayRef));
        if (userData?.NameOnCard) {
          saveState("NameOnCard", userData.NameOnCard);
          saveState("firstname", userData.FirstName);
          saveState("lastname", userData.LastName);
          saveState("SessionID", userData.SessionID);
          saveState("Poiemail", userData.Poiemail);
          saveState("PDBankName", userData.PDBankName);
          saveState("PDBankIFSC", userData.PDBankIFSC);
          saveState("PDBankAccountNo", userData.PDBankAccountNo);
          saveState("virtualAccountNo", userData.AccountNo);
          saveState("lastlogin", userData.LastSuccessfulLogin);
          saveState("DailyCount", userData.DailyUsageCapCount);
          saveState("DailyAmount", userData.DailyUsageCapAmount);
          saveState("UsageCap", userData.isUsageCapEnabled);
          saveState("Is_KYC_Done", userData.Is_KYC_Done);
          saveState("address1", userData.Poadist);
          saveState("address2", userData.Poaloc);
          saveState("zipcode", userData.Poapc);
          saveState("state", userData.Poastate);
          saveState("city", userData.Is_KYC_Done);
          saveState("dob", userData.Poidob);
          saveState("pan", userData.PAN);
          saveState("WalletTypeID", userData.WalletTypeID);
        }
      }
    } catch (err) {
      console.error("Failed to parse CustomerProfile:", err);
    }

    try {
      const bankMasterString = responseData.Info?.[0]?.BankMaster;
      if (bankMasterString) {
        const bankMasterData = JSON.parse(bankMasterString);
        localStorage.setItem("BankMaster", JSON.stringify(bankMasterData));
      }
    } catch (err) {
      console.error("BankMaster parse error:", err);
    }

    try {
      const bannerString = responseData.Info?.[0]?.banner_popup;
      if (bannerString) {
        const bannerData = JSON.parse(bannerString);
        sessionStorage.setItem("bannerData", JSON.stringify(bannerData));
      }
    } catch (err) {
      console.error("Banner parse error:", err);
    }

    const info = responseData.Info?.[0];
    if (info) {
      dispatch(setBbpsUrls(info));
    }

    if (userData) {
      dispatch(setUser(userData));
      localStorage.setItem("userData", JSON.stringify(userData));
    }

    const subWalletString = responseData.Info?.[0]?.SubWallets;
    if (subWalletString && subWalletString.trim() !== "") {
      try {
        const parsed = JSON.parse(subWalletString);
        dispatch(setSubWallets(parsed));
      } catch (error) {
        dispatch(setSubWallets([]));
      }
    } else {
      dispatch(setSubWallets([]));
    }

    setTimeout(() => {
      login();
      navigate("/dashboard", { replace: true });
    }, 1000);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (verifying) return;
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) {
      toast.error("Please enter a 6-digit OTP.", {
        style: errorToastStyle,
        position: "top-center",
      });
      return;
    }

    if (!messageRefId || !mobileNumber) {
      toast.error("Session data missing. Please restart login process.", {
        style: errorToastStyle,
        position: "top-center",
      });
      return;
    }
    setVerifying(true);

    try {
      const payload = {
        EntityID: entity_id,
        ApplicationType,
        Platform,
        ReqType: "One_loginvalidotpV2",
        InputMessageRefID: messageRefId,
        OTP: enteredOtp,
        DeviceID:
          "d8StqY8kSK65YINoATrGvH:APA91bG7sxFgCsnOqubyCAzm_H5mnI64uDXFOJ0AH78IMWSEJkZC7PmTTZkh4zG9nBnI0icROb35jKDgD7tRzBN38PVWZhzpmeav_K6K31dy_jdlprtbaktd",
        Statement: "LoginValidOTP",
        MachineID: ip,
      };

      const response = await api.post(
        `${baseapi}/auth/loginvalidateotpV2`,
        payload,
      );
      console.log("VALIDATE full response →", response.data);
      console.log("Response_Code value →", response.data.Response_Code, "type →", typeof response.data.Response_Code);

      const token = response.data.token;
      setToken(token);

      if (response.data.Response_Code === "1000") {
        try {
          const p = JSON.parse(response.data.Info?.[0]?.CustomerProfile)[0];
          console.log("SETUP FLAGS →", {
            SetTPIN: p.SetTPIN,
            SetRupayPIN: p.SetRupayPIN,
            SetRupaySecurityQNS: p.SetRupaySecurityQNS,
            needsSetup: needsSetup(response.data),
          });
        } catch (e) {
          console.log("profile parse failed", e);
        }
        const walletToken = response.data.WalletToken;
        localStorage.setItem("SessionWalletToken", walletToken);

        if (mobileNumber) {
          sessionStorage.setItem("mobileNumber", mobileNumber);
        }

        // ✅ store security questions now (from OTP response) for the Forgot flow
        const rupayQs = response.data.Info?.[0]?.RupaySecurityQuestions;
        if (rupayQs) {
          sessionStorage.setItem("RupaySecurityQuestions", rupayQs);
        }

        try {
          const profile = JSON.parse(response.data.Info[0].CustomerProfile)[0];
          console.log("SETUP profile RupayRefNo →", profile?.RupayRefNo, "SessionID →", profile?.SessionID);
          if (profile?.SessionID) sessionStorage.setItem("SessionID", profile.SessionID);
          if (profile?.RupayRefNo) sessionStorage.setItem("RupayRefNo", profile.RupayRefNo);
        } catch (e) {
          console.error("setup profile parse failed", e);
        }

        // ✅ if TPIN / RupayPIN / security-questions not set → show setup page
        const steps = getPendingSetupSteps(response.data);
        if (steps.length > 0) {
          setPendingLoginData(response.data);
          setPendingSteps(steps);
          setScreen(steps[0]); // first pending step, whichever it is
          return;
        }

        const validateResponse = await axios.post(VALIDATE_TOKEN_URL, {
          token: walletToken,
        });

        if (
          validateResponse.data?.status === "VALID" &&
          !isExpired(validateResponse.data?.expiryTime)
        ) {
          setScreen("tpin");
        } else {
          setScreen("otp");
        }
      } else if (response.data.Response_Code === "5555") {
        // ── Merchant user ─────────────────────────────────────────────────
        // Store WalletToken for Bearer auth in merchant APIs
        if (response.data.WalletToken) {
          localStorage.setItem("SessionWalletToken", response.data.WalletToken);
          sessionStorage.setItem("MerchantToken", response.data.WalletToken);
        }

        // Store mobileNumber
        if (mobileNumber) {
          sessionStorage.setItem("mobileNumber", mobileNumber);
        }

        // Store NameOnCard + SessionID from CustomerProfile
        try {
          const profileStr = response.data.Info?.[0]?.CustomerProfile;
          if (profileStr) {
            const profile = JSON.parse(profileStr)[0];
            if (profile?.NameOnCard) {
              saveState("NameOnCard", profile.NameOnCard);
            }
            if (profile?.SessionID) {
              saveState("SessionID", profile.SessionID);
              sessionStorage.setItem("SessionID", JSON.stringify(profile.SessionID));
            }
          }
        } catch (err) {
          console.error("Merchant profile parse error:", err);
        }

        // Store merchant-specific fields
        if (response.data.MerchantID) {
          sessionStorage.setItem("MerchantID", String(response.data.MerchantID));
        }
        if (response.data.MerchantName) {
          sessionStorage.setItem("MerchantName", response.data.MerchantName);
        }

        // ✅ Store SettledAmount and UnSettledAmount directly from login response
        // These are shown in the merchant dashboard stat cards
        if (response.data.SettledAmount !== undefined) {
          sessionStorage.setItem("merchantSettledAmount", String(response.data.SettledAmount));
        }
        if (response.data.UnSettledAmount !== undefined) {
          sessionStorage.setItem("merchantUnsettledAmount", String(response.data.UnSettledAmount));
        }

        sessionStorage.setItem("isMerchant", "true");

        navigate("/merchant-dashboard", { replace: true });

      } else {
        toast.error(response.data.Message || "OTP validation failed", {
          style: errorToastStyle,
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
    } catch (error) {
      toast.error("Error while validating OTP", {
        style: errorToastStyle,
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleTpinChange = (e, index) => {
    const value = e.target.value;
    if (!/^\d?$/.test(value)) return;

    const newTpin = [...tpin];
    newTpin[index] = value;
    setTpin(newTpin);

    if (value && index < tpin.length - 1) {
      const next = document.getElementById(`tpin-${index + 1}`);
      next?.focus();
    }
  };

  const handleTpinKeyDown = (e, index) => {
    if (e.key === "Backspace" && !tpin[index] && index > 0) {
      const prev = document.getElementById(`tpin-${index - 1}`);
      prev?.focus();
    }
  };

  const handleTpinVerify = async (e) => {
    e.preventDefault();
    if (verifyingTpin) return;

    const enteredTpin = tpin.join("");
    if (enteredTpin.length !== 4) {
      toast.error("Please enter your 4-digit TPIN.", {
        style: errorToastStyle,
        position: "top-center",
      });
      return;
    }

    const walletToken = localStorage.getItem("SessionWalletToken");
    const mobileForTpin = mobileNumber;

    if (!mobileForTpin) {
      toast.error("Mobile number not found. Please login with OTP.", {
        style: errorToastStyle,
        position: "top-center",
      });
      setScreen("otp");
      return;
    }
    const geoForTpin = geo && geo.trim() !== "" ? geo : STATIC_GEOLOCATION;
    setVerifyingTpin(true);
    try {
      const payload = {
        ReqType: "LoginWithTPIN",
        Mobilenumber: mobileForTpin,
        Statement: "ValidateTPIN",
        TPIN: enteredTpin,
        EntityID: entity_id,
        MachineID: ip,
        Platform,
        GeoLocation: geoForTpin,
        IsTPINEnabled: "True",
        WalletToken: walletToken,
      };

      const response = await axios.post(LOGIN_WITH_TPIN_URL, payload);

      if (response.data.Response_Code === "1000") {
        const token = response.data.token;
        setToken(token);
        handleLoginSuccess(response.data);
      } else {
        // Token expired / logged in on another device
        if (response.data.Message === "Token not found or inactive") {
          // Clear old session
          localStorage.removeItem("SessionWalletToken");
          sessionStorage.removeItem("mobileNumber");

          setActiveMobile("");

          toast.warning(
            "You are already logged in on another device. Please login with OTP.",
            {
              position: "top-center",
              autoClose: 5000,
            },
          );
          // Show mobile number page
          setScreen("switch-number");
          return;
        }
        setTpin(["", "", "", ""]);

        toast.error(response.data.Message, {
          style: errorToastStyle,
          position: "top-center",
        });

        document.getElementById("tpin-0")?.focus();
      }
    } catch (error) {
      toast.error("Error while validating TPIN", {
        style: errorToastStyle,
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
      });
    } finally {
      setVerifyingTpin(false);
    }
  };

  const [sendingOtpInstead, setSendingOtpInstead] = useState(false);

  const handleUseOtpInstead = async () => {
    if (!mobileNumber) {
      toast.error("Mobile number not found. Please login again.", {
        style: errorToastStyle,
        position: "top-center",
      });
      navigate("/login", { replace: true });
      return;
    }

    setSendingOtpInstead(true);
    try {
      const payload = {
        ReqType: "One_userlogin",
        MobileNumber: mobileNumber,
        EntityID: entity_id,
        SSOID: "-",
        MemberID: "26384597149",
        Statement: "LoginWithOTP",
        MachineID: ip,
        Platform,
        Geolocation: geo,
      };

      const response = await api.post(
        `${baseapi}/auth/walletexistance`,
        payload,
      );
      const { Response_Code, Message, MessageRefID } = response.data;

      if (Response_Code === "1110") {
        dispatch(
          setLoginMeta({
            isLoggedIn: true,
            messageRefId: MessageRefID,
            mobileNumber,
          }),
        );

        toast.success("OTP sent successfully!", {
          style: successToastStyle,
          position: "top-center",
        });

        setOtp(["", "", "", "", "", ""]);
        setTpin(["", "", "", ""]);
        setTimer(60);
        setShowResend(false);
        setScreen("otp");
      } else {
        toast.error(Message || "Could not send OTP. Please try again.", {
          style: errorToastStyle,
          position: "top-center",
        });
      }
    } catch {
      toast.error("Error sending OTP. Please try again.", {
        style: errorToastStyle,
        position: "top-center",
      });
    } finally {
      setSendingOtpInstead(false);
    }
  };

  const handleSendAltOtp = async (e) => {
    e.preventDefault();

    if (altPhone.length !== 10 || !/^[6-9]/.test(altPhone)) {
      setAltPhoneError(true);
      toast.error("Enter a valid 10-digit mobile number starting with 6-9", {
        style: errorToastStyle,
        position: "top-center",
      });
      return;
    }
    setAltPhoneError(false);
    setSendingAltOtp(true);

    try {
      const payload = {
        ReqType: "One_userlogin",
        MobileNumber: altPhone,
        EntityID: entity_id,
        SSOID: "-",
        MemberID: "26384597149",
        Statement: "LoginWithOTP",
        MachineID: ip,
        Platform,
        Geolocation: geo,
      };

      const response = await api.post(
        `${baseapi}/auth/walletexistance`,
        payload,
      );
      const { Response_Code, Message, MessageRefID } = response.data;

      if (Response_Code === "1110") {
        localStorage.removeItem("SessionWalletToken");

        sessionStorage.setItem("mobileNumber", altPhone);
        dispatch(
          setLoginMeta({
            isLoggedIn: true,
            messageRefId: MessageRefID,
            mobileNumber: altPhone,
          }),
        );

        setActiveMobile(altPhone);

        toast.success("OTP sent successfully!", {
          style: successToastStyle,
          position: "top-center",
        });

        setOtp(["", "", "", "", "", ""]);
        setTimer(60);
        setShowResend(false);
        setAltPhone("");
        setScreen("otp");
      } else {
        toast.error(
          Message || "Could not send OTP. Check the number and try again.",
          {
            style: errorToastStyle,
            position: "top-center",
          },
        );
      }
    } catch {
      toast.error("Error sending OTP. Please try again.", {
        style: errorToastStyle,
        position: "top-center",
      });
    } finally {
      setSendingAltOtp(false);
    }
  };

  // ── Forgot TPIN: Step 1 — Generate OTP ─────────────────────────────────────
  const handleForgotTpin = async () => {
    if (!mobileNumber) {
      toast.error("Mobile number not found. Please login again.", {
        style: errorToastStyle,
        position: "top-center",
      });
      return;
    }

    setGeneratingForgotOtp(true);
    try {
      const payload = {
        ReqType: "MT_Reg_GenerateOTP",
        Mobilenumber: mobileNumber,
        Purpose: "GenerateOTP",
        EntityID: entity_id,
        TrxnAmount: "0",
        Platform,
        ApplicationType,
        GeoLocation: geo,
        MachineID: ip,
      };

      const response = await axios.post(GENERATE_OTP_URL, payload);

      if (response.data.Response_Code === "1000") {
        setForgotOtpMsgRefId(response.data.MessageRefID);
        setForgotOtp(["", "", "", "", "", ""]);
        setForgotOtpTimer(60);
        setForgotOtpShowResend(false);
        setScreen("forgot-tpin-otp");
        toast.success("OTP sent to your registered mobile number.", {
          style: successToastStyle,
          position: "top-center",
        });
      } else {
        toast.error(response.data.Message || "Failed to send OTP.", {
          style: errorToastStyle,
          position: "top-center",
        });
      }
    } catch {
      toast.error("Error sending OTP. Please try again.", {
        style: errorToastStyle,
        position: "top-center",
      });
    } finally {
      setGeneratingForgotOtp(false);
    }
  };

  // ── Forgot TPIN: Resend OTP ─────────────────────────────────────────────────
  const handleResendForgotOtp = async () => {
    setGeneratingForgotOtp(true);
    try {
      const payload = {
        ReqType: "MT_Reg_GenerateOTP",
        Mobilenumber: mobileNumber,
        Purpose: "GenerateOTP",
        EntityID: entity_id,
        TrxnAmount: "0",
        Platform,
        ApplicationType,
        GeoLocation: geo,
        MachineID: ip,
      };

      const response = await axios.post(GENERATE_OTP_URL, payload);

      if (response.data.Response_Code === "1000") {
        setForgotOtpMsgRefId(response.data.MessageRefID);
        setForgotOtp(["", "", "", "", "", ""]);
        setForgotOtpTimer(60);
        setForgotOtpShowResend(false);
        toast.success("OTP resent successfully!", {
          style: successToastStyle,
          position: "top-center",
        });
      } else {
        toast.error(response.data.Message || "Failed to resend OTP.", {
          style: errorToastStyle,
          position: "top-center",
        });
      }
    } catch {
      toast.error("Error resending OTP. Please try again.", {
        style: errorToastStyle,
        position: "top-center",
      });
    } finally {
      setGeneratingForgotOtp(false);
    }
  };

  // ── Forgot TPIN: Step 2 — Validate OTP ─────────────────────────────────────
  const handleVerifyForgotOtp = async (e) => {
    e.preventDefault();
    if (verifyingForgotOtp) return;

    const enteredOtp = forgotOtp.join("");
    if (enteredOtp.length !== 6) {
      toast.error("Please enter the 6-digit OTP.", {
        style: errorToastStyle,
        position: "top-center",
      });
      return;
    }

    setVerifyingForgotOtp(true);
    try {
      const payload = {
        ReqType: "MT_Reg_ValidateOTP",
        Mobilenumber: mobileNumber,
        MessageRefID: forgotOtpMsgRefId,
        OTP: enteredOtp,
        Platform,
        EntityID: entity_id,
        GeoLocation: geo,
        MachineID: ip,
        DeviceID: "fd2d6da32e083a77",
        VersionName: "1.5",
        VersionCode: 7,
      };

      const response = await axios.post(VALIDATE_OTP_URL, payload);

      if (response.data.Response_Code === "1000") {
        setNewTpin(["", "", "", ""]);
        setConfirmTpin(["", "", "", ""]);
        setScreen("forgot-tpin-set");
      } else {
        toast.error(response.data.Message || "Invalid OTP. Please try again.", {
          style: errorToastStyle,
          position: "top-center",
        });
      }
    } catch {
      toast.error("Error validating OTP. Please try again.", {
        style: errorToastStyle,
        position: "top-center",
      });
    } finally {
      setVerifyingForgotOtp(false);
    }
  };

  // ── Forgot TPIN: Step 3 — Set new TPIN ─────────────────────────────────────
  const handleSetNewTpin = async (e) => {
    e.preventDefault();
    if (settingTpin) return;

    const newTpinStr = newTpin.join("");
    const confirmTpinStr = confirmTpin.join("");

    if (newTpinStr.length !== 4) {
      toast.error("Please enter a 4-digit TPIN.", {
        style: errorToastStyle,
        position: "top-center",
      });
      return;
    }

    if (newTpinStr !== confirmTpinStr) {
      toast.error("TPINs do not match. Please try again.", {
        style: errorToastStyle,
        position: "top-center",
      });
      // Clear confirm field
      setConfirmTpin(["", "", "", ""]);
      document.getElementById("confirm-tpin-0")?.focus();
      return;
    }

    setSettingTpin(true);
    try {
      const payload = {
        ReqType: "MT_TPIN_Management",
        Mobilenumber: Number(mobileNumber),
        Statement: "AddTPIN",
        TPIN: newTpinStr,
        OldTPIN: "",
        IsTPINEnabled: "True",
        Platform,
        EntityID: entity_id,
        GeoLocation: geo,
        MachineID: ip,
      };

      const response = await axios.post(TPIN_MANAGEMENT_URL, payload);

      if (response.data.Response_Code === "1710") {
        toast.success(response.data.Message || "TPIN created successfully!", {
          
          style: successToastStyle,
          position: "top-center",
        });
        // Reset TPIN fields and go back to TPIN login screen
        setTpin(["", "", "", ""]);
        setTimeout(() => setScreen("tpin"), 1200);
      } else {
        toast.error(response.data.Message || "Failed to set TPIN.", {
          style: errorToastStyle,
          position: "top-center",
        });
      }
    } catch {
      toast.error("Error setting TPIN. Please try again.", {
        style: errorToastStyle,
        position: "top-center",
      });
    } finally {
      setSettingTpin(false);
    }
  };

  // ── Generic PIN field change handler factory ────────────────────────────────
  const makePinChangeHandler =
    (setter, currentArr, maxLen, prefix) => (e, index) => {
      const value = e.target.value;
      if (!/^\d?$/.test(value)) return;
      const updated = [...currentArr];
      updated[index] = value;
      setter(updated);
      if (value && index < maxLen - 1) {
        document.getElementById(`${prefix}-${index + 1}`)?.focus();
      }
    };

  const makePinKeyDownHandler = (currentArr, prefix) => (e, index) => {
    if (e.key === "Backspace" && !currentArr[index] && index > 0) {
      document.getElementById(`${prefix}-${index - 1}`)?.focus();
    }
  };

  const makeForgotOtpChangeHandler = () => (e, index) => {
    const value = e.target.value;
    if (!/^\d?$/.test(value)) return;
    const updated = [...forgotOtp];
    updated[index] = value;
    setForgotOtp(updated);
    if (value && index < 5) {
      document.getElementById(`forgot-otp-${index + 1}`)?.focus();
    }
  };

  const makeForgotOtpKeyDownHandler = () => (e, index) => {
    if (e.key === "Backspace" && !forgotOtp[index] && index > 0) {
      document.getElementById(`forgot-otp-${index - 1}`)?.focus();
    }
  };

  const enteredOtp = otp.join("");
  const isDisabled = verifying || enteredOtp.length !== 6;

  const enteredTpin = tpin.join("");
  const isTpinDisabled = verifyingTpin || enteredTpin.length !== 4;

  const forgotOtpEntered = forgotOtp.join("");
  const isForgotOtpDisabled =
    verifyingForgotOtp || forgotOtpEntered.length !== 6;

  const isSetTpinDisabled =
    settingTpin ||
    newTpin.join("").length !== 4 ||
    confirmTpin.join("").length !== 4;

  const WHY = [
    "Instant bill payments across 500+ billers",
    "RuPay card with contactless payments",
    "Bank-grade 256-bit SSL encryption",
    "RBI compliant & BBPS certified",
  ];

  // ── Shared style helpers ────────────────────────────────────────────────────
  const pinFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 3,
      background: "#f8fafc",
      transition: "0.2s",
      "& fieldset": { borderColor: "#d1d5db" },
      "&:hover fieldset": { borderColor: "#16a34a" },
      "&.Mui-focused fieldset": { borderColor: "#16a34a", borderWidth: "2px" },
    },
  };

  const primaryBtnSx = (disabled) => ({
    height: "52px",
    background: disabled
      ? "#d1d5db"
      : "linear-gradient(135deg, #16a34a, #22c55e)",
    color: disabled ? "#888" : "#fff",
    borderRadius: 3,
    fontWeight: 700,
    textTransform: "none",
    fontSize: "15px",
    "&:hover": { filter: "brightness(0.95)" },
  });

  useEffect(() => {
    if (screen === "tpin") {
      setTpin(["", "", "", ""]);
    }
  }, [screen]);

  // Canonical order of the setup flow
const SETUP_ORDER = ["setup-tpin", "setup-security", "setup-rupay-pin"];

// A flag counts as "already set" ONLY when it is truly true.
// Handles booleans (true/false) AND strings ("true"/"false"/"True"/"False") and 1/"1".
const isFlagTrue = (v) =>
  v === true ||
  v === 1 ||
  (typeof v === "string" && v.trim().toLowerCase() === "true");

// Returns only the steps whose flag is NOT true, in canonical order.
const getPendingSetupSteps = (responseData) => {
  try {
    const profileStr = responseData.Info?.[0]?.CustomerProfile;
    if (!profileStr) return [];
    const profile = JSON.parse(profileStr)[0] || {};

    // step is pending when its flag is not truly true
    const stepByFlag = {
      "setup-tpin": !isFlagTrue(profile.SetTPIN),
      "setup-security": !isFlagTrue(profile.SetRupaySecurityQNS),
      "setup-rupay-pin": !isFlagTrue(profile.SetRupayPIN),
    };

    return SETUP_ORDER.filter((step) => stepByFlag[step]);
  } catch {
    return [];
  }

};

// true if at least one step needs doing
const needsSetup = (responseData) => getPendingSetupSteps(responseData).length > 0;

  // given the current setup screen, return the next pending screen (or null if done)
  const getNextSetupStep = (currentStep) => {
    const idx = pendingSteps.indexOf(currentStep);
    if (idx === -1) return pendingSteps[0] ?? null; // safety
    return pendingSteps[idx + 1] ?? null;
  };

  // Move to the next pending setup step; if none remain, finish login → dashboard
  const advanceSetup = (currentStep) => {
    const next = getNextSetupStep(currentStep);
    if (next) {
      setScreen(next);
    } else if (pendingLoginData) {
      handleLoginSuccess(pendingLoginData); // all setup done → dashboard
    } else {
      setScreen("tpin");
    }
  };



  const renderBrandPanel = () => (
    <Box
      sx={{
        width: "48%",
        flexShrink: 0,
        background:
          "linear-gradient(160deg, #064e3b 0%, #065f46 30%, #16a34a 70%, #22c55e 100%)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        px: 8,
        py: 6,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -80,
          right: -80,
          width: 320,
          height: 320,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.05)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -60,
          left: -40,
          width: 240,

          height: 240,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.06)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "40%",
          right: 40,
          width: 100,
          height: 100,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.04)",
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 6 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src="/Assets/ta_walletlogo.png"
              alt="TA Wallet"
              sx={{
                width: 30,
                height: 30,
                objectFit: "contain",
                filter: "none",
              }}
              onError={(e) => {
                console.error("Logo not found");
                e.target.style.display = "none";
              }}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 800,
                color: "white",
                lineHeight: 1,
              }}
            >
              TA Wallet
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: "rgba(255,255,255,0.7)",
                fontWeight: 500,
              }}
            >
              One App · All Payments
            </Typography>
          </Box>
        </Box>

        <Typography
          sx={{
            fontSize: 40,
            fontWeight: 900,
            color: "white",
            lineHeight: 1.1,
            mb: 2,
            letterSpacing: "-1.5px",
          }}
        >
          Welcome
          <br />
          back.
        </Typography>
        <Typography
          sx={{
            fontSize: 15,
            color: "rgba(255,255,255,0.75)",
            mb: 6,
            lineHeight: 1.75,
          }}
        >
          Your trusted digital wallet is waiting. Sign in to manage payments,
          bills, and your RuPay card.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {WHY.map((w, i) => (
            <Box
              key={i}
              sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
            >
              <CheckCircleIcon
                sx={{ fontSize: 18, color: "#4ade80", flexShrink: 0 }}
              />
              <Typography
                sx={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}
              >
                {w}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          component="img"
          src="/Assets/loginimg.png"
          alt="Wallet illustration"
          sx={{
            width: "100%",
            maxWidth: 280,
            mt: 6,
            objectFit: "contain",
            filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.2))",
            mx: "auto",
            display: "block",
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      </Box>
    </Box>
  );

  const renderLogo = () => (
    <Box display="flex" alignItems="center">
      <img
        src="/Assets/logota.jpg"
        alt="TA Logo"
        style={{ width: "150px", height: "80px", objectFit: "contain" }}
      />
    </Box>
  );

  // ── Reusable PIN row renderer ───────────────────────────────────────────────
  const renderPinFields = (
    arr,
    prefix,
    type = "text",
    size = isMobile ? "35px" : "45px",
    onChange,
    onKeyDown,
  ) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: { xs: 1, sm: 1.5 },
        mb: 3,
      }}
    >
      {arr.map((digit, index) => (
        <TextField
          key={index}
          id={`${prefix}-${index}`}
          type={type}
          value={digit}
          autoComplete="off"
          onChange={(e) => onChange(e, index)}
          onKeyDown={(e) => onKeyDown(e, index)}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: "center",
              fontSize: "22px",
              fontWeight: "700",
              width: size,
              height: size,
              padding: 0,
            },
          }}
          sx={pinFieldSx}
        />
      ))}
    </Box>
  );

  if (screen === "checking") {
    return (
      <Grid
        container
        height="100vh"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="body1" color="text.secondary">
          Checking your session...
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnHover={false}
        draggable
        theme="colored"
      />

      <Grid container height="100vh">
        {!isMobile && renderBrandPanel()}

        <Grid
          size={{ xs: 12, md: 6 }}
          item
          xs={12}
          md={6}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="white"
          sx={{ p: { xs: 3, md: 3 }, px: { xs: 0, md: 4 } }}
        >
          {/* ─────────────── OTP SCREEN ─────────────── */}
          {screen === "otp" ? (
            <Box
              component="form"
              onSubmit={handleVerify}
              sx={{
                width: { xs: "90%", sm: "420px" },
                background: "#fff",
                borderRadius: 4,
                p: 4,
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            >
              {renderLogo()}
              <Typography variant="h5" fontWeight="bold">
                Enter verification code
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                OTP has been sent to <b>{maskMobile(mobileNumber)}</b>{" "}
                <Link to="/login" style={{ color: "#16a34a" }}>
                  Edit
                </Link>
              </Typography>

              {renderPinFields(
                otp,
                "otp",
                "text",
                isMobile ? "35px" : "45px",
                handleChange,
                handleKeyDown,
              )}

              <Button
                type="submit"
                fullWidth
                disabled={isDisabled}
                sx={primaryBtnSx(isDisabled)}
              >
                {verifying ? "Verifying..." : "Verify OTP"}
              </Button>

              <Box textAlign="left" mt={1}>
                {!showResend ? (
                  <Typography fontSize={13} color="text.secondary">
                    Resend OTP in <b>{timer}s</b>
                  </Typography>
                ) : (
                  <Typography
                    fontSize={14}
                    sx={{
                      color: "#16a34a",
                      cursor: "pointer",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                    onClick={handleResendOTP}
                  >
                    Resend OTP
                  </Typography>
                )}
              </Box>
              <Box sx={{ height: "1px", background: "#e0e0e0", my: 2 }} />
            </Box>
          ) : screen === "switch-number" ? (
            /* ─────────────── SWITCH NUMBER SCREEN ─────────────── */
            <Box
              component="form"
              onSubmit={handleSendAltOtp}
              sx={{
                width: { xs: "90%", sm: "420px" },
                background: "#fff",
                borderRadius: 4,
                p: 4,
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            >
              {renderLogo()}
              <Typography variant="h5" fontWeight="bold" mb={0.5}>
                Login with another number
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Enter a different mobile number to receive a fresh OTP.
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#374151",
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Mobile Number
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Enter mobile number"
                  value={altPhone}
                  autoComplete="off"
                  autoFocus
                  onChange={(e) => {
                    const n = e.target.value.replace(/\D/g, "");
                    if (n.length <= 10) setAltPhone(n);
                    if (altPhoneError) setAltPhoneError(false);
                  }}
                  error={altPhoneError}
                  helperText={
                    altPhoneError
                      ? "Enter a valid number starting with 6-9"
                      : ""
                  }
                  inputProps={{ maxLength: 10, inputMode: "numeric" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            bgcolor: "#f0fdf4",
                            px: 1.2,
                            py: 0.5,
                            borderRadius: "7px",
                            color: "#16a34a",
                            fontWeight: 700,
                            fontSize: 13,
                            whiteSpace: "nowrap",
                          }}
                        >
                          🇮🇳 +91
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#f8fafc",
                      borderRadius: "12px",
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={sendingAltOtp || altPhone.length !== 10}
                  sx={{
                    px: 2.5,
                    minWidth: 96,
                    borderRadius: "12px",
                    whiteSpace: "nowrap",
                    fontWeight: 700,
                    background:
                      sendingAltOtp || altPhone.length !== 10
                        ? "#e2e8f0"
                        : "linear-gradient(135deg, #16a34a, #22c55e)",
                    color:
                      sendingAltOtp || altPhone.length !== 10
                        ? "#94a3b8"
                        : "white",
                    boxShadow:
                      sendingAltOtp || altPhone.length !== 10
                        ? "none"
                        : "0 4px 14px rgba(22,163,74,0.3)",
                  }}
                >
                  {sendingAltOtp ? "..." : "Get OTP"}
                </Button>
              </Box>
              <Box sx={{ height: "1px", background: "#e0e0e0", mb: 2 }} />
              <Typography
                fontSize={14}
                sx={{ color: "#64748b", cursor: "pointer", fontWeight: 500 }}
                onClick={() => setScreen("tpin")}
              >
                ← Back
              </Typography>
            </Box>
          ) : screen === "forgot-tpin-otp" ? (
            /* ─────────────── FORGOT TPIN — OTP VERIFY ─────────────── */
            <Box
              component="form"
              onSubmit={handleVerifyForgotOtp}
              sx={{
                width: { xs: "90%", sm: "420px" },
                background: "#fff",
                borderRadius: 4,
                p: 4,
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            >
              {renderLogo()}

              {/* Step indicator */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "#16a34a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: "white" }}
                  >
                    1
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, height: 2, bgcolor: "#e5e7eb" }}>
                  <Box
                    sx={{ width: "0%", height: "100%", bgcolor: "#16a34a" }}
                  />
                </Box>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "#e5e7eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: "#9ca3af" }}
                  >
                    2
                  </Typography>
                </Box>
              </Box>

              <Typography variant="h5" fontWeight="bold">
                Verify your identity
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                An OTP has been sent to <b>{maskMobile(mobileNumber)}</b>. Enter
                it below to reset your TPIN.
              </Typography>

              {renderPinFields(
                forgotOtp,
                "forgot-otp",
                "text",
                isMobile ? "35px" : "45px",
                makeForgotOtpChangeHandler(),
                makeForgotOtpKeyDownHandler(),
              )}

              <Button
                type="submit"
                fullWidth
                disabled={isForgotOtpDisabled}
                sx={primaryBtnSx(isForgotOtpDisabled)}
              >
                {verifyingForgotOtp ? "Verifying..." : "Verify OTP"}
              </Button>

              <Box textAlign="left" mt={1}>
                {!forgotOtpShowResend ? (
                  <Typography fontSize={13} color="text.secondary">
                    Resend OTP in <b>{forgotOtpTimer}s</b>
                  </Typography>
                ) : (
                  <Typography
                    fontSize={14}
                    sx={{
                      color: generatingForgotOtp ? "#94a3b8" : "#16a34a",
                      cursor: generatingForgotOtp ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                    onClick={
                      !generatingForgotOtp ? handleResendForgotOtp : undefined
                    }
                  >
                    {generatingForgotOtp ? "Sending..." : "Resend OTP"}
                  </Typography>
                )}
              </Box>

              <Box sx={{ height: "1px", background: "#e0e0e0", my: 2 }} />
              <Typography
                fontSize={14}
                sx={{ color: "#64748b", cursor: "pointer", fontWeight: 500 }}
                onClick={() => setScreen("tpin")}
              >
                ← Back to Login
              </Typography>
            </Box>
          ) : screen === "forgot-tpin-set" ? (
            /* ─────────────── FORGOT TPIN — SET NEW TPIN ─────────────── */
            <Box
              component="form"
              onSubmit={handleSetNewTpin}
              sx={{
                width: { xs: "90%", sm: "420px" },
                background: "#fff",
                borderRadius: 4,
                p: 4,
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            >
              {renderLogo()}

              {/* Step indicator */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "#16a34a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 14, color: "white" }} />
                </Box>
                <Box sx={{ flex: 1, height: 2, bgcolor: "#16a34a" }} />
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "#16a34a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: "white" }}
                  >
                    2
                  </Typography>
                </Box>
              </Box>

              <Typography variant="h5" fontWeight="bold">
                Set new TPIN
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Choose a new 4-digit TPIN for your account.
              </Typography>

              {/* New TPIN */}
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#374151",
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                New TPIN
              </Typography>
              {renderPinFields(
                newTpin,
                "new-tpin",
                "password",
                isMobile ? "40px" : "50px",
                makePinChangeHandler(setNewTpin, newTpin, 4, "new-tpin"),
                makePinKeyDownHandler(newTpin, "new-tpin"),
              )}

              {/* Confirm TPIN */}
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#374151",
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Confirm TPIN
              </Typography>
              {renderPinFields(
                confirmTpin,
                "confirm-tpin",
                "password",
                isMobile ? "40px" : "50px",
                makePinChangeHandler(
                  setConfirmTpin,
                  confirmTpin,
                  4,
                  "confirm-tpin",
                ),
                makePinKeyDownHandler(confirmTpin, "confirm-tpin"),
              )}

              <Button
                type="submit"
                fullWidth
                disabled={isSetTpinDisabled}
                sx={primaryBtnSx(isSetTpinDisabled)}
              >
                {settingTpin ? "Saving..." : "Set TPIN"}
              </Button>

              <Box sx={{ height: "1px", background: "#e0e0e0", my: 2 }} />
              <Typography
                fontSize={14}
                sx={{ color: "#64748b", cursor: "pointer", fontWeight: 500 }}
                onClick={() => setScreen("forgot-tpin-otp")}
              >
                ← Back
              </Typography>
            </Box>
          ) : screen === "setup-tpin" ? (
            <CreateTpinSetup
              mobileNumber={mobileNumber}
              geo={geo}
              onSuccess={() => advanceSetup("setup-tpin")}
              onBack={() => setScreen("otp")}
            />
          ) : screen === "setup-security" ? (
            <SetSecurityQuestionsSetup
              mobileNumber={mobileNumber}
              sessionId={sessionStorage.getItem("SessionID") || ""}
              rupayRefNo={sessionStorage.getItem("RupayRefNo") || ""}
              onSuccess={() => advanceSetup("setup-security")}
              onBack={() => setScreen("setup-tpin")}
            />
          ) : screen === "setup-rupay-pin" ? (
            <SetRupayPinSetup
              mobileNumber={mobileNumber}
              sessionId={sessionStorage.getItem("SessionID") || ""}
              rupayRefNo={sessionStorage.getItem("RupayRefNo") || ""}
              onSuccess={() => advanceSetup("setup-rupay-pin")}
              onBack={() => setScreen("setup-security")}
            />
          ) : (
            /* ─────────────── TPIN SCREEN ─────────────── */
            <Box
              component="form"
              onSubmit={handleTpinVerify}
              sx={{
                width: { xs: "90%", sm: "420px" },
                background: "#fff",
                borderRadius: 4,
                p: 4,
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            >
              {renderLogo()}
              <Typography variant="h5" fontWeight="bold">
                Enter your TPIN
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Welcome back, <b>{maskMobile(mobileNumber)}</b>. Enter your
                4-digit TPIN to continue.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: { xs: 1.5, sm: 2 },
                  mb: 3,
                }}
              >
                {tpin.map((digit, index) => (
                  <TextField
                    key={index}
                    id={`tpin-${index}`}
                    type="password"
                    value={digit}
                    autoComplete="off"
                    onChange={(e) => handleTpinChange(e, index)}
                    onKeyDown={(e) => handleTpinKeyDown(e, index)}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: "center",
                        fontSize: "22px",
                        fontWeight: "700",
                        width: isMobile ? "40px" : "50px",
                        height: isMobile ? "40px" : "50px",
                        padding: 0,
                      },
                    }}
                    sx={pinFieldSx}
                  />
                ))}
              </Box>

              <Button
                type="submit"
                fullWidth
                disabled={isTpinDisabled}
                sx={primaryBtnSx(isTpinDisabled)}
              >
                {verifyingTpin ? "Verifying..." : "Login with TPIN"}
              </Button>

              {/* Secondary actions */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  mt: 1.5,
                }}
              >
                <Typography
                  fontSize={14}
                  sx={{
                    color: sendingOtpInstead ? "#94a3b8" : "#16a34a",
                    cursor: sendingOtpInstead ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    textDecoration: "underline",
                    pointerEvents: sendingOtpInstead ? "none" : "auto",
                  }}
                  onClick={handleUseOtpInstead}
                >
                  {sendingOtpInstead
                    ? "Sending OTP..."
                    : "Login with OTP instead"}
                </Typography>
                <Typography
                  fontSize={14}
                  sx={{
                    color: "#16a34a",
                    cursor: "pointer",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                  onClick={() => setScreen("switch-number")}
                >
                  Login with another number
                </Typography>
                {/* ── Forgot TPIN ── */}
                <Typography
                  fontSize={14}
                  sx={{
                    color: generatingForgotOtp ? "#94a3b8" : "#ef4444",
                    cursor: generatingForgotOtp ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    textDecoration: "underline",
                    pointerEvents: generatingForgotOtp ? "none" : "auto",
                  }}
                  onClick={!generatingForgotOtp ? handleForgotTpin : undefined}
                >
                  {generatingForgotOtp ? "Sending OTP..." : "Forgot TPIN?"}
                </Typography>
              </Box>

              <Box sx={{ height: "1px", background: "#e0e0e0", my: 2 }} />
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default OtpPage;